import { useMemo, useState, type ComponentType, type SVGProps } from "react";
import { CalendarX2, CheckCheck, Globe, Layers, PenLine, Send } from "lucide-react";
import {
  totalPostsPerMonth,
  type CadenceCounts,
  type CalendarPlanSlot,
  type PlanBriefInput,
} from "@/types/contentCalendar.type";
import type { BriefRailInfo } from "@/types/productionBrief.type";
import { MONTH_NAMES } from "../calendar.utils";
import { clientTitle } from "../postState";
import { useRegeneratePlan } from "../api/useContentCalendar";
import { AcknowledgeRow, CalendarConfirm } from "../month/CalendarSheet";
import { Consequence } from "./DeleteSlotModal";

type Icon = ComponentType<SVGProps<SVGSVGElement> & { strokeWidth?: number }>;

function listOf(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

function count(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * Re-plan a month at any stage (I9s0B): the agent throws the current posts away
 * and lays the month out again from scratch.
 *
 * It spares nothing, past approval, mid-review, after sign-off and after posts
 * have gone live, so the confirmation counts every kind of work in the month
 * from the posts themselves (their ideas and comments, the briefs the host has
 * read, the approved files, what the client has on their page or already
 * approved, what is scheduled or live, the quick posts added by hand) rather
 * than describing the mild case. The danger action is the one filled button
 * and waits for the tick (Calendar / Re-plan acknowledge row).
 *
 * Dialog / Confirm as the Pen draws it: no brief fields (direction for the new
 * plan goes through the chat). Opened from the cadence editor, the numbers typed there can come
 * along: `beforeReplan` saves them as the month's cadence first, so the new plan
 * is laid out against what is on screen. If that save fails nothing is deleted.
 */
export default function RegenerateMonthModal({
  brandId,
  planId,
  planStatus,
  targetYear,
  targetMonth,
  slots,
  cadence = null,
  beforeReplan,
  onClose,
  clientName = null,
  briefBySlot,
  phone = false,
  onConfirmed,
}: {
  /**
   * Given, the confirmed brief goes here instead of the re-plan request. The
   * dev harness passes a no-op so its Re-plan can never reach the API.
   */
  onConfirmed?: (brief: PlanBriefInput) => void;
  brandId: string;
  planId: string;
  planStatus: string;
  targetYear: number;
  targetMonth: number;
  slots: CalendarPlanSlot[];
  /** A cadence change that rides along with this re-plan, shown on the warning. */
  cadence?: CadenceCounts | null;
  /** Runs after confirming and before the month is wiped. A throw stops the re-plan. */
  beforeReplan?: () => Promise<void>;
  onClose: () => void;
  clientName?: string | null;
  /** The month's briefs, when the host has read them, to count the ones that go. */
  briefBySlot?: Map<string, BriefRailInfo>;
  /** On a phone the dialog is a sheet floating 8px in. */
  phone?: boolean;
}) {
  const [confirmed, setConfirmed] = useState(false);
  const regenerate = useRegeneratePlan(brandId, planId);

  const monthName = MONTH_NAMES[targetMonth - 1];
  const client = clientTitle(clientName);
  const existing = slots.length;
  // A planned month is APPROVED from the moment its run lands, so it is on the
  // warned side straight away; the legacy PENDING_APPROVAL is a month that was
  // planned but whose ideas never started.
  const hasDownstreamWork = !["DRAFT", "PENDING_APPROVAL", "GENERATING"].includes(planStatus);
  const busy = regenerate.isPending;
  const needsAcknowledgement = existing > 0 && !confirmed;

  const stakes = useMemo(() => {
    let published = 0;
    let scheduled = 0;
    let quick = 0;
    let ideas = 0;
    let comments = 0;
    let briefs = 0;
    let files = 0;
    let withClient = 0;
    let approved = 0;
    for (const slot of slots) {
      if (slot.origin === "DIRECT") quick++;
      if (slot.status === "IN_CLIENT_REVIEW") withClient++;
      if (slot.status === "CLIENT_APPROVED") approved++;
      if (slot.deliverable) files++;
      if (briefBySlot?.has(slot.id)) briefs++;
      for (const idea of slot.ideas ?? []) {
        ideas++;
        comments += idea.comments?.length ?? 0;
      }
      const publications = slot.deliverable?.publications ?? [];
      if (publications.some((p) => p.status === "PUBLISHED")) published++;
      else if (publications.some((p) => p.status === "SCHEDULED" || p.status === "PUBLISHING")) scheduled++;
    }
    return { published, scheduled, quick, ideas, comments, briefs, files, withClient, approved };
  }, [slots, briefBySlot]);

  const items: { key: string; icon: Icon; text: string }[] = [];
  if (existing > 0) {
    // "5 posts go, with their ideas, briefs and comments." (I9s0B)
    const work = [
      stakes.ideas > 0 ? "ideas" : null,
      stakes.briefs > 0 ? "briefs" : null,
      stakes.comments > 0 ? "comments" : null,
      stakes.files > 0 ? "approved files" : null,
    ].filter((part): part is string => !!part);
    items.push({
      key: "posts",
      icon: Layers,
      text: `${count(existing, "post", "posts")} ${existing === 1 ? "goes" : "go"}${work.length > 0 ? `, with ${existing === 1 ? "its" : "their"} ${listOf(work)}` : ""}.`,
    });
    if (stakes.withClient > 0) {
      items.push({
        key: "client",
        icon: Send,
        text: `${stakes.withClient === 1 ? "1 of them is" : `${stakes.withClient} of them are`} with ${client} right now. ${stakes.withClient === 1 ? "It leaves" : "They leave"} ${client}’s page.`,
      });
    }
    if (stakes.approved > 0) {
      items.push({ key: "approved", icon: CheckCheck, text: `Anything ${client} already approved in ${monthName} goes too.` });
    }
    if (stakes.scheduled > 0) {
      items.push({
        key: "scheduled",
        icon: CalendarX2,
        text: `${count(stakes.scheduled, "post is", "posts are")} scheduled to go out. The schedule is cancelled.`,
      });
    }
    if (stakes.published > 0) {
      items.push({
        key: "published",
        icon: Globe,
        text: `${count(stakes.published, "post", "posts")} already went live. What went out stays up there; take it down on the platform.`,
      });
    }
    if (stakes.quick > 0) {
      items.push({
        key: "quick",
        icon: PenLine,
        text: `${count(stakes.quick, "quick post", "quick posts")} added by hand ${stakes.quick === 1 ? "goes" : "go"} too. The agent does not make ${stakes.quick === 1 ? "it" : "them"} again.`,
      });
    }
    if (!hasDownstreamWork) {
      items.push({ key: "hand", icon: PenLine, text: "Titles, formats, pillars and days changed by hand." });
    }
  }

  const answersGo = stakes.withClient > 0 || stakes.approved > 0;
  const submit = async () => {
    if (busy || needsAcknowledgement) return;
    // Act on the confirm. The month shows as planning the moment the request
    // goes out, and a failure rolls it back with its own toast.
    onClose();
    if (beforeReplan) {
      try {
        await beforeReplan();
      } catch {
        // Its own mutation toasted, and nothing has been deleted yet.
        return;
      }
    }
    const brief: PlanBriefInput = {};
    if (onConfirmed) onConfirmed(brief);
    else regenerate.mutate(brief);
  };

  return (
    <CalendarConfirm
      phone={phone}
      title={`Re-plan ${monthName}?`}
      label={`Re-plan ${monthName} ${targetYear}?`}
      description={
        existing === 0
          ? `The agent drafts ${monthName} from the strategy and the scope. It has no posts yet, so nothing is replaced.`
          : `The agent drafts ${monthName} again from the strategy and the scope. Everything in it now is replaced.`
      }
      onClose={onClose}
      busy={busy}
      width="max-w-[560px]"
      cancelLabel={`Keep ${monthName}`}
      confirmLabel={`Re-plan ${monthName}`}
      onConfirm={() => void submit()}
      confirmDisabled={needsAcknowledgement}
    >
      {items.length > 0 ? (
        <ul className="flex flex-col gap-3">
          {items.map((item) => (
            <Consequence key={item.key} icon={item.icon}>
              {item.text}
            </Consequence>
          ))}
        </ul>
      ) : null}
      {cadence ? (
        <p className="text-[13px] leading-[1.55] text-co-muted">
          The new plan uses the scope you just set, {totalPostsPerMonth(cadence)} posts: {cadence.staticPostsPerMonth} static,{" "}
          {cadence.verticalVideoPostsPerMonth} vertical video and {cadence.horizontalVideoPostsPerMonth} horizontal video.
          It is saved as {monthName}&rsquo;s own scope first.
        </p>
      ) : null}
      {existing > 0 ? (
        <AcknowledgeRow checked={confirmed} onChange={setConfirmed} disabled={busy}>
          I understand {monthName}&rsquo;s work{answersGo ? ` and ${client}’s answers are` : " is"} replaced.
        </AcknowledgeRow>
      ) : null}
    </CalendarConfirm>
  );
}
